/**
 * Profit & Loss Excel Service - Browser Version
 * ==============================================
 *
 * Generates a 2-sheet workbook (Cover + Profit & Loss) styled to match the
 * cash flow Excel export. The P&L sheet renders the same hierarchical data
 * shown on the page: section banners → indented account rows → section totals,
 * plus computed subtotals (Gross Profit, Operating Income, Net Other Income,
 * Net Income).
 */

import ExcelJS from 'exceljs';

// ─── Color palette ─────────────────────────────────────────────────────────
const COVER_COLORS = {
  GRAY_TEXT:   'FF6B7785',
  GRAY_BORDER: 'FFD0D7E2',
};

const PL_COLORS = {
  NAVY:                'FF1F3864',
  NAVY_MID:            'FF2F5496',
  HEADER_FILL:         'FFE8E8E8',
  TOTAL_FILL:          'FFD9E1F2',
  SUBTOTAL_FILL:       'FFEEF2FA',
  GRAND_TOTAL_FILL:    'FFFCE4D6',
  TEXT_DARK:           'FF1A1A1A',
  TEXT_NAVY:           'FF1F3864',
  TEXT_MUTED:          'FF737373',
  WHITE:               'FFFFFFFF',
  BORDER_GRAY:         'FFD0D7E2',
};

const NUM_FORMAT_CURRENCY = '$#,##0_);[Red]($#,##0)';

// ─── Branding helpers ──────────────────────────────────────────────────────
function hexToArgb(hex) {
  if (!hex) return null;
  const clean = hex.replace('#', '').toUpperCase();
  if (clean.length === 6) return `FF${clean}`;
  if (clean.length === 8) return clean;
  return null;
}

function buildBrandingColors(branding = {}) {
  return {
    primary:   hexToArgb(branding.primaryColor)   || PL_COLORS.NAVY,
    secondary: hexToArgb(branding.secondaryColor) || PL_COLORS.NAVY_MID,
    accent:    hexToArgb(branding.accentColor)    || 'FF4A90E2',
  };
}

// ─── Date / range formatters (UTC-based to match the page) ────────────────
function formatDatePretty(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr + 'T00:00:00Z');
  return dt.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

// Page-style range label: "Jan 1, 2024 – Apr 29, 2024" (end exclusive → minus 1 day)
function fmtRangeLabel(range) {
  if (!range?.startDate || !range?.endDate) return '';
  const s = new Date(range.startDate + 'T00:00:00Z');
  const e = new Date(range.endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 86400000);
  const opts = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} – ${eMinus1.toLocaleDateString('en-US', opts)}`;
}

// "Jan 2024" or "Jan – Apr 2024" — used as the column header in Total view.
function fmtColumnRange(period) {
  if (!period?.startDate || !period?.endDate) return '';
  const s = new Date(period.startDate + 'T00:00:00Z');
  const e = new Date(period.endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 86400000);
  const opts = { month: 'short', year: 'numeric', timeZone: 'UTC' };
  const sLabel = s.toLocaleDateString('en-US', opts);
  const eLabel = eMinus1.toLocaleDateString('en-US', opts);
  return sLabel === eLabel ? sLabel : `${sLabel} – ${eLabel}`;
}

function granularityLabel(g) {
  if (g === 'monthly') return 'Monthly';
  if (g === 'quarterly') return 'Quarterly';
  return 'Total';
}

// Use portrait when the P&L is narrow (few periods), landscape otherwise.
// Threshold of 5 keeps single-period and short trend views vertical (matches
// US Letter aspect on print/PDF) while wider trend views go landscape.
function pageOrientationForPeriods(periods) {
  return (periods?.length || 0) < 5 ? 'portrait' : 'landscape';
}

// ─── Cover sheet ──────────────────────────────────────────────────────────
async function buildCoverSheet(wb, pnlData, metadata) {
  const { primary } = buildBrandingColors(metadata?.branding);
  const colors = { ...COVER_COLORS, NAVY: primary };
  const logoUrl = metadata?.branding?.logoDownloadUrl
    || metadata?.branding?.logoPresignedUrl
    || null;

  const ws = wb.addWorksheet('Cover', {
    properties: { tabColor: { argb: primary } },
  });
  ws.views = [{ showGridLines: false }];

  ws.getColumn(1).width = 2;
  ws.getColumn(2).width = 75;
  ws.getColumn(3).width = 58;

  const workspaceName = metadata?.workspaceName || '';
  const reportName = metadata?.statementName || 'Profit & Loss';
  const granLabel = granularityLabel(pnlData?.granularity);
  const periods = pnlData?.periods || [];
  const range = pnlData?.range || {};
  const compRange = pnlData?.comparisonRange;
  const pullDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });

  const addEmpty = (count = 1) => {
    for (let i = 0; i < count; i++) ws.addRow([]);
  };

  // Top spacer
  addEmpty(1);

  // Navy accent bar (rows 2-3, cols B–C)
  for (let r = 0; r < 2; r++) {
    const row = ws.addRow([]);
    for (let c = 2; c <= 3; c++) {
      row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.NAVY } };
    }
  }

  addEmpty(1);

  // Workspace eyebrow
  const wsRow = ws.addRow(['', workspaceName]);
  wsRow.getCell(2).font = { name: 'Calibri', size: 13, color: { argb: colors.GRAY_TEXT } };
  wsRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  wsRow.height = 22;

  addEmpty(1);

  // Title
  const titleRow = ws.addRow(['', reportName]);
  titleRow.getCell(2).font = { name: 'Calibri', size: 26, bold: true, color: { argb: colors.NAVY } };
  titleRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  titleRow.height = 40;

  // Subtitle: e.g. "Total View  |  Jan 1, 2024 – Apr 29, 2024"
  const subtitleText = `${granLabel} View  |  ${fmtRangeLabel(range)}`;
  const subRow = ws.addRow(['', subtitleText]);
  subRow.getCell(2).font = { name: 'Calibri', size: 14, color: { argb: colors.GRAY_TEXT } };
  subRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  subRow.height = 24;

  addEmpty(2);

  // Divider
  const divRow = ws.addRow([]);
  for (let c = 2; c <= 3; c++) {
    divRow.getCell(c).border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };
  }

  addEmpty(1);

  // Metadata rows
  const metaFont = { name: 'Calibri', size: 11, color: { argb: colors.GRAY_TEXT } };
  const metaValueFont = { name: 'Calibri', size: 11, color: { argb: 'FF333333' } };

  const metaItems = [
    ['Report Date', pullDate],
    ['View',        granLabel],
    ['Period Range', fmtRangeLabel(range)],
    ['Periods',     `${periods.length}`],
  ];
  if (compRange?.startDate && compRange?.endDate) {
    metaItems.push(['Compared To', fmtRangeLabel(compRange)]);
  }

  metaItems.forEach(([label, value]) => {
    const row = ws.addRow(['', label, value]);
    row.getCell(2).font = metaFont;
    row.getCell(3).font = metaValueFont;
    row.height = 20;
  });

  addEmpty(2);

  const divRow2 = ws.addRow([]);
  for (let c = 2; c <= 3; c++) {
    divRow2.getCell(c).border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };
  }

  addEmpty(1);

  // ── Logo (right-aligned in column C, vertically centered in rows 5-10)
  if (logoUrl) {
    try {
      const useCredentials = logoUrl.includes('/api/orgs/logo-file/');
      const resp = await fetch(logoUrl, useCredentials ? { credentials: 'include' } : undefined);
      if (resp.ok) {
        const blob = await resp.blob();
        const mimeType = (blob.type || resp.headers.get('content-type') || 'image/png')
          .split(';')[0]
          .toLowerCase();
        if (['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(mimeType)) {
          const ext = mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpeg'
            : mimeType.includes('gif') ? 'gif' : 'png';
          const arrayBuffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
          const base64 = btoa(binary);
          const imageId = wb.addImage({
            base64: `data:${mimeType};base64,${base64}`,
            extension: ext,
          });

          const objectUrl = URL.createObjectURL(blob);
          try {
            const dimensions = await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve({
                width: img.naturalWidth || 1,
                height: img.naturalHeight || 1,
              });
              img.onerror = () => reject(new Error('Unable to measure logo dimensions'));
              img.src = objectUrl;
            });

            const maxWidthPx = 180;
            const maxHeightPx = 100;
            const scale = Math.min(
              maxWidthPx / dimensions.width,
              maxHeightPx / dimensions.height,
              1,
            );
            const fittedWidth = Math.round(dimensions.width * scale);
            const fittedHeight = Math.round(dimensions.height * scale);

            const columnCPixels = Math.round((ws.getColumn(3).width || 8.43) * 7 + 5);
            const boxTopRow = 4;
            const boxHeightPixels = [5, 6, 7, 8, 9, 10]
              .reduce((sum, rowNum) => sum + Math.round(((ws.getRow(rowNum).height || 15) * 96) / 72), 0);
            const topOffsetPx = Math.max(0, Math.round((boxHeightPixels - fittedHeight) / 2));
            const leftOffsetPx = Math.max(0, columnCPixels - fittedWidth);
            const EMUS_PER_PIXEL = 9525;

            ws.addImage(imageId, {
              tl: {
                col: 2,
                row: boxTopRow,
                nativeCol: 2,
                nativeRow: boxTopRow,
                nativeColOff: Math.round(leftOffsetPx * EMUS_PER_PIXEL),
                nativeRowOff: Math.round(topOffsetPx * EMUS_PER_PIXEL),
              },
              ext: { width: fittedWidth, height: fittedHeight },
              editAs: 'oneCell',
            });
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        }
      }
    } catch (e) {
      console.warn('Logo embed failed:', e?.message);
    }
  }

  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
  };

  return ws;
}

// ─── Profit & Loss sheet ──────────────────────────────────────────────────
function buildPnLSheet(wb, pnlData, metadata) {
  const { primary } = buildBrandingColors(metadata?.branding);
  const isTrend = pnlData?.granularity === 'monthly' || pnlData?.granularity === 'quarterly';
  const periods = pnlData?.periods || [];
  const showTotal = periods.length > 1;
  const compRange = pnlData?.comparisonRange;
  const showCompare = !isTrend && !!(compRange?.startDate && compRange?.endDate);

  const ws = wb.addWorksheet('Profit & Loss', {
    properties: { tabColor: { argb: primary } },
  });

  // Freeze the header rows + account name column.
  ws.views = [{ showGridLines: false, state: 'frozen', xSplit: 2, ySplit: 6 }];

  const LABEL_COL = 2;
  const FIRST_DATE_COL = 3;
  const lastPeriodCol = FIRST_DATE_COL + periods.length - 1;
  const totalCol = showTotal ? lastPeriodCol + 1 : null;
  const cmpCol = showCompare ? (totalCol || lastPeriodCol) + 1 : null;
  const varCol = showCompare ? cmpCol + 1 : null;
  const lastCol = varCol || cmpCol || totalCol || lastPeriodCol;

  // Column widths
  ws.getColumn(1).width = 2;
  ws.getColumn(2).width = 50;
  for (let c = FIRST_DATE_COL; c <= lastCol; c++) {
    ws.getColumn(c).width = 16;
  }

  // ── Row 1: Title
  const titleRow = ws.addRow(['', 'Profit & Loss']);
  const titleCell = titleRow.getCell(LABEL_COL);
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: primary } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  titleRow.height = 26;

  // ── Row 2: Subtitle
  const workspaceName = metadata?.workspaceName ? `${metadata.workspaceName}  •  ` : '';
  const subtitleText = `${workspaceName}For the period ${fmtRangeLabel(pnlData?.range)}`
    + (showCompare ? `  vs  ${fmtRangeLabel(compRange)}` : '');
  const subRow = ws.addRow(['', subtitleText]);
  subRow.getCell(LABEL_COL).font = {
    name: 'Calibri', size: 10, italic: true, color: { argb: PL_COLORS.TEXT_MUTED },
  };
  subRow.getCell(LABEL_COL).alignment = { horizontal: 'left', vertical: 'middle' };
  subRow.height = 18;

  // Row 3: blank
  ws.addRow([]);

  // ── Row 4: Eyebrow labels above comparison columns (when applicable)
  const eyebrowRow = ws.addRow([]);
  eyebrowRow.height = 16;
  if (showCompare) {
    const cmpEyebrow = eyebrowRow.getCell(cmpCol);
    cmpEyebrow.value = 'COMPARISON';
    cmpEyebrow.font = { name: 'Calibri', size: 9, bold: true, color: { argb: PL_COLORS.TEXT_MUTED } };
    cmpEyebrow.alignment = { horizontal: 'center', vertical: 'middle' };

    const varEyebrow = eyebrowRow.getCell(varCol);
    varEyebrow.value = 'VARIANCE';
    varEyebrow.font = { name: 'Calibri', size: 9, bold: true, color: { argb: PL_COLORS.TEXT_MUTED } };
    varEyebrow.alignment = { horizontal: 'center', vertical: 'middle' };
  }

  // ── Row 5: Column headers
  const headerRow = ws.addRow(['', 'ACCOUNT']);
  headerRow.height = 22;

  const setHeaderCell = (cell, text, align = 'center') => {
    cell.value = text;
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: PL_COLORS.TEXT_DARK } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PL_COLORS.HEADER_FILL } };
    cell.alignment = { horizontal: align, vertical: 'middle' };
    cell.border = { bottom: { style: 'thin', color: { argb: PL_COLORS.NAVY } } };
  };

  setHeaderCell(headerRow.getCell(LABEL_COL), 'ACCOUNT', 'left');
  for (let i = 0; i < periods.length; i++) {
    const text = isTrend ? (periods[i].label || '') : fmtColumnRange(periods[i]);
    setHeaderCell(headerRow.getCell(FIRST_DATE_COL + i), text);
  }
  if (showTotal) setHeaderCell(headerRow.getCell(totalCol), 'TOTAL');
  if (showCompare) {
    setHeaderCell(headerRow.getCell(cmpCol), fmtColumnRange(compRange));
    setHeaderCell(headerRow.getCell(varCol), 'VARIANCE');
  }

  // Row 6: blank spacer
  ws.addRow([]);

  // ── Body helpers ─────────────────────────────────────────────────────────
  const writeNumberCell = (cell, val) => {
    if (val == null || isNaN(val)) {
      cell.value = null;
    } else {
      cell.value = Math.round(val);
    }
    cell.numFmt = NUM_FORMAT_CURRENCY;
    cell.alignment = { horizontal: 'right', vertical: 'middle' };
  };

  const applyRowFill = (rowNum, argb) => {
    for (let c = LABEL_COL; c <= lastCol; c++) {
      ws.getCell(rowNum, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
    }
  };

  const writeRowValues = (row, values, total, totalCmp, totalVar, opts = {}) => {
    const baseFont = opts.font || { name: 'Calibri', size: 10, color: { argb: PL_COLORS.TEXT_DARK } };
    if (opts.fillArgb) applyRowFill(row.number, opts.fillArgb);

    for (let i = 0; i < periods.length; i++) {
      const cell = row.getCell(FIRST_DATE_COL + i);
      writeNumberCell(cell, values?.[i]);
      cell.font = baseFont;
    }
    if (showTotal) {
      const cell = row.getCell(totalCol);
      writeNumberCell(cell, total);
      cell.font = baseFont;
    }
    if (showCompare) {
      const cmpCell = row.getCell(cmpCol);
      writeNumberCell(cmpCell, totalCmp);
      cmpCell.font = baseFont;

      const varCell = row.getCell(varCol);
      writeNumberCell(varCell, totalVar);
      varCell.font = baseFont;
    }
  };

  const addSectionBanner = (label) => {
    const row = ws.addRow(['', String(label || '').toUpperCase()]);
    row.height = 22;
    const labelCell = row.getCell(LABEL_COL);
    labelCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: PL_COLORS.WHITE } };
    labelCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    applyRowFill(row.number, primary);
    return row;
  };

  const addAccountRow = (node, depth) => {
    const indent = '  '.repeat(depth + 1); // base indent so accounts sit clearly under banner
    const row = ws.addRow(['', `${indent}${node.name}`]);
    row.getCell(LABEL_COL).font = {
      name: 'Calibri', size: 10, color: { argb: PL_COLORS.TEXT_DARK },
    };
    row.getCell(LABEL_COL).alignment = { horizontal: 'left', vertical: 'middle' };
    writeRowValues(row, node.values, node.total, node.total_comparison, node.total_variance);
    if (node.children && node.children.length) {
      for (const child of node.children) addAccountRow(child, depth + 1);
    }
  };

  const addSectionTotal = (section) => {
    const row = ws.addRow(['', section.total_label || `Total ${section.label}`]);
    row.height = 20;
    const labelFont = {
      name: 'Calibri', size: 10, bold: true, color: { argb: PL_COLORS.TEXT_NAVY },
    };
    row.getCell(LABEL_COL).font = labelFont;
    row.getCell(LABEL_COL).alignment = { horizontal: 'left', vertical: 'middle' };
    writeRowValues(row, section.values, section.total, section.total_comparison, section.total_variance, {
      font: labelFont,
      fillArgb: PL_COLORS.TOTAL_FILL,
    });
    return row;
  };

  const addComputedTotalRow = (totalNode, kind = 'subtotal') => {
    if (!totalNode) return null;
    const row = ws.addRow(['', totalNode.label]);
    row.height = 20;
    const isGrand = kind === 'grand';
    const fillArgb = isGrand ? PL_COLORS.GRAND_TOTAL_FILL : PL_COLORS.SUBTOTAL_FILL;
    const fontColor = isGrand ? PL_COLORS.TEXT_DARK : PL_COLORS.TEXT_NAVY;
    const labelFont = { name: 'Calibri', size: 10, bold: true, color: { argb: fontColor } };

    row.getCell(LABEL_COL).font = labelFont;
    row.getCell(LABEL_COL).alignment = { horizontal: 'left', vertical: 'middle' };
    writeRowValues(row, totalNode.values, totalNode.total, totalNode.total_comparison, totalNode.total_variance, {
      font: labelFont,
      fillArgb,
    });

    if (isGrand) {
      for (let c = LABEL_COL; c <= lastCol; c++) {
        const existing = ws.getCell(row.number, c).border || {};
        ws.getCell(row.number, c).border = {
          ...existing,
          top:    { style: 'thin',   color: { argb: PL_COLORS.NAVY } },
          bottom: { style: 'double', color: { argb: PL_COLORS.NAVY } },
        };
      }
    }
    return row;
  };

  // ── Render body ──────────────────────────────────────────────────────────
  const sections = pnlData?.sections || [];
  const totals = pnlData?.totals || {};
  const sectionByKey = (key) => sections.find(s => s.key === key);

  const renderSection = (section) => {
    if (!section) return;
    addSectionBanner(section.label);
    for (const node of section.accounts || []) addAccountRow(node, 0);
    addSectionTotal(section);
    ws.addRow([]);
  };

  if (sections.length === 0) {
    const emptyRow = ws.addRow(['', 'No P&L activity in this range.']);
    emptyRow.getCell(LABEL_COL).font = {
      name: 'Calibri', size: 11, italic: true, color: { argb: PL_COLORS.TEXT_MUTED },
    };
  } else {
    renderSection(sectionByKey('income'));
    renderSection(sectionByKey('cogs'));
    if (sectionByKey('cogs')) {
      addComputedTotalRow(totals.gross_profit, 'subtotal');
      ws.addRow([]);
    }

    renderSection(sectionByKey('expense'));
    if (sectionByKey('expense') || sectionByKey('cogs') || sectionByKey('income')) {
      addComputedTotalRow(totals.operating_income, 'subtotal');
      ws.addRow([]);
    }

    renderSection(sectionByKey('other_income'));
    renderSection(sectionByKey('other_expense'));
    if (sectionByKey('other_income') || sectionByKey('other_expense')) {
      addComputedTotalRow(totals.net_other_income, 'subtotal');
      ws.addRow([]);
    }

    addComputedTotalRow(totals.net_income, 'grand');
  }

  ws.pageSetup = {
    orientation: pageOrientationForPeriods(periods),
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printTitlesRow: '1:6',
    margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
  };

  return ws;
}

// ─── Public API ────────────────────────────────────────────────────────────

export async function generatePnLExcelBlob(pnlData, metadata = {}) {
  const wb = new ExcelJS.Workbook();
  await buildCoverSheet(wb, pnlData, metadata);
  buildPnLSheet(wb, pnlData, metadata);

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export async function downloadPnLExcel(pnlData, filename = 'profit_and_loss', metadata = {}) {
  try {
    const blob = await generatePnLExcelBlob(pnlData, metadata);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`✅ Downloaded ${filename}.xlsx`);
  } catch (error) {
    console.error('❌ Error generating Profit & Loss Excel:', error);
    throw error;
  }
}
